import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Lock, LogOut } from "lucide-react";

import { request } from "@/lib/api";
import { track } from "@/lib/telemetry";
import { useAuth } from "@/state/AuthContext";
import { ChangePasswordSection } from "@/features/administration/components/profile/ChangePasswordSection";
import { normalizePlatformRole, PLATFORM_ROLE } from "@/utils/roles";

const profileSchema = z.object({
  firstName: z.string().min(1, "First name is required").max(100),
  lastName: z.string().min(1, "Last name is required").max(100),
});

type ProfileForm = z.infer<typeof profileSchema>;

type AccountProfile = {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  profilePicture: string | null;
  platformRole: string;
  organizationName?: string | null;
  isEmailVerified: boolean;
  createdAt: string;
};

type SaveFeedback = "idle" | "saving" | "saved" | "error";

function roleBadgeLabel(platformRole: string): string {
  const normalized = normalizePlatformRole(platformRole);
  if (normalized === PLATFORM_ROLE.ADMIN) return "Admin";
  if (normalized === PLATFORM_ROLE.VIEWER) return "Viewer";
  return "Member";
}

function initialsFromProfile(p: AccountProfile): string {
  const first = p.firstName?.trim()?.[0];
  const last = p.lastName?.trim()?.[0];
  if (first && last) return `${first}${last}`.toUpperCase();
  if (first) return first.toUpperCase();
  return (p.email?.[0] || "?").toUpperCase();
}

function displayFullName(p: AccountProfile): string {
  const name = [p.firstName, p.lastName].filter(Boolean).join(" ").trim();
  return name || p.email;
}

export default function AdminProfilePage() {
  const { user, logout } = useAuth();
  const queryClient = useQueryClient();
  const hydratedRef = useRef(false);
  const [saveFeedback, setSaveFeedback] = useState<SaveFeedback>("idle");
  const savedClearRef = useRef<ReturnType<typeof setTimeout> | undefined>();

  const handleLogout = async () => {
    track("user.logout", { userId: user?.id });
    await logout();
  };

  const profileQuery = useQuery({
    queryKey: ["auth", "account-profile"],
    queryFn: () => request.get<AccountProfile>("/auth/profile"),
  });

  const profileForm = useForm<ProfileForm>({
    resolver: zodResolver(profileSchema),
    defaultValues: { firstName: "", lastName: "" },
  });

  useEffect(() => {
    if (!profileQuery.data) return;
    profileForm.reset({
      firstName: profileQuery.data.firstName || "",
      lastName: profileQuery.data.lastName || "",
    });
    hydratedRef.current = true;
  }, [profileQuery.data, profileForm]);

  useEffect(() => {
    return () => {
      if (savedClearRef.current) clearTimeout(savedClearRef.current);
    };
  }, []);

  const updateProfile = useMutation({
    mutationFn: (body: ProfileForm) => request.patch<AccountProfile>("/auth/profile", body),
    onMutate: () => {
      setSaveFeedback("saving");
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["auth", "account-profile"] });
      setSaveFeedback("saved");
      if (savedClearRef.current) clearTimeout(savedClearRef.current);
      savedClearRef.current = setTimeout(() => setSaveFeedback("idle"), 2500);
    },
    onError: () => {
      setSaveFeedback("error");
    },
  });

  const onSave = profileForm.handleSubmit((values) => {
    updateProfile.mutate(values);
  });

  const p = profileQuery.data;
  const dirty = profileForm.formState.isDirty;

  return (
    <div className="space-y-8">
      <header>
        <h1 className="text-2xl font-semibold text-gray-900">Profile</h1>
        <p className="mt-1 text-sm text-gray-600">Manage your personal account details.</p>
        <p className="mt-1 text-xs font-medium text-neutral-500" aria-live="polite">
          {saveFeedback === "saving"
            ? "Saving…"
            : saveFeedback === "saved"
              ? "Saved"
              : saveFeedback === "error"
                ? "Couldn’t save changes. Try again."
                : null}
        </p>
      </header>

      {profileQuery.isLoading && (
        <div className="rounded-lg border border-gray-200 bg-white p-6 text-sm text-gray-500">Loading…</div>
      )}
      {profileQuery.isError && (
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" role="alert">
          Could not load profile.{" "}
          <button
            type="button"
            className="font-medium underline"
            onClick={() => void profileQuery.refetch()}
          >
            Retry
          </button>
        </div>
      )}

      {p && (
        <>
          <section className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-900">Account</h2>
            <p className="mt-1 text-sm text-gray-500">Your identity in this organization.</p>

            <div className="mt-6 flex items-center gap-4">
              {p.profilePicture ? (
                <img
                  src={p.profilePicture}
                  alt=""
                  className="h-16 w-16 shrink-0 rounded-full object-cover"
                />
              ) : (
                <div
                  className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-blue-800 to-blue-500 text-xl font-semibold text-white"
                  aria-hidden
                >
                  {initialsFromProfile(p)}
                </div>
              )}
              <div className="min-w-0 space-y-1">
                <p className="truncate text-base font-semibold text-gray-900">{displayFullName(p)}</p>
                {p.organizationName ? (
                  <p className="truncate text-sm text-gray-700">{p.organizationName}</p>
                ) : null}
                <span className="inline-flex rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-800">
                  {roleBadgeLabel(p.platformRole)}
                </span>
              </div>
            </div>
          </section>

          <section className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-900">Personal details</h2>
            <p className="mt-1 text-sm text-gray-500">Update how your name appears across Zephix.</p>

            <form className="mt-6 space-y-6" onSubmit={onSave}>
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700" htmlFor="firstName">
                    First name
                  </label>
                  <input
                    id="firstName"
                    className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    {...profileForm.register("firstName")}
                  />
                  {profileForm.formState.errors.firstName && (
                    <p className="mt-1 text-xs text-red-600">
                      {profileForm.formState.errors.firstName.message}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700" htmlFor="lastName">
                    Last name
                  </label>
                  <input
                    id="lastName"
                    className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    {...profileForm.register("lastName")}
                  />
                  {profileForm.formState.errors.lastName && (
                    <p className="mt-1 text-xs text-red-600">
                      {profileForm.formState.errors.lastName.message}
                    </p>
                  )}
                </div>
              </div>

              <div className="rounded-md border border-gray-200 bg-gray-50 px-4 py-3">
                <div className="flex items-start gap-2">
                  <Lock className="mt-0.5 h-4 w-4 shrink-0 text-gray-500" aria-hidden />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Email</p>
                    <p className="text-sm text-gray-700">{p.email}</p>
                    <p className="mt-1 text-xs text-gray-500">
                      {p.isEmailVerified ? "Verified · cannot be changed here." : "Cannot be changed here."}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <button
                  type="submit"
                  disabled={!dirty || updateProfile.isPending}
                  className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {updateProfile.isPending ? "Saving…" : "Save changes"}
                </button>
                {saveFeedback === "error" ? (
                  <button
                    type="button"
                    className="text-sm font-medium text-indigo-600 hover:text-indigo-800"
                    onClick={() => void onSave()}
                  >
                    Try again
                  </button>
                ) : null}
              </div>
            </form>
          </section>

          <ChangePasswordSection />
        </>
      )}

      {/* Founder: Log out only here and as the last avatar-menu item — not in the work sidebar. */}
      <section className="border-t border-gray-200 pt-6">
        <button
          type="button"
          data-testid="settings-profile-logout"
          onClick={() => void handleLogout()}
          className="inline-flex min-h-[44px] items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500"
        >
          <LogOut className="h-4 w-4" aria-hidden />
          Log out
        </button>
      </section>
    </div>
  );
}
